import { faEdit } from "@fortawesome/pro-regular-svg-icons/faEdit";
import { faReplyAll } from "@fortawesome/pro-solid-svg-icons/faReplyAll";
import { Grid, Table, TableBody, TableCell, TableRow, Typography } from "@mui/material";
import * as api from "api/Core";
import { DefaultListParameters } from "assets/appConfig";
import { AppMessageBox } from "components/AppMessageBox";
import IconButtonCancel from "components/Buttons/IconButtonCancel";
import { SpinnerIndicator } from "components/CallServer";
import DashListBox, { DashBoxListItem } from "components/Dashboard/DashListBox";
import IconCreatePublication from "components/Icons/IconCreatePublication";
import MuiMessageBox from "components/MuiExtras/MessageBox/MuiMessageBox";
import MuiContainer from "components/MuiExtras/MuiContainer";
import MuiLink from "components/MuiExtras/MuiLink";
import MuiPagination from "components/MuiExtras/MuiPagination";
import MuiPaper from "components/MuiExtras/MuiPaper";
import MuiMuted from "components/MuiExtras/Typography/MuiMuted";
import MuiReverse from "components/MuiExtras/Typography/MuiReverse";
import { Formaters } from "lib/Formarters";
import { DashboardGraphModel, DashboardUserEvolutionParticiationListModel, DashboardUserEvolutionParticipationColumn, DashboardUserEvolutionParticipationResumeModel, PagedResult } from "models/ApiModels";
import moment from "moment";
import * as React from "react";
import Session from "services/Session";
import { RenderGraphGmtInfo } from "views/Dashboards/components/RenderGraphGmtInfo";
import { RenderLineGraph } from "views/Dashboards/components/RenderLineGraph";
import { CommonProps } from "views/Dashboards/UserEvolutionDashboard";
import { getPublicationUrl } from "views/Timeline/Components/FeedHelpers";

/////////////////////////////////////////////////////////////////////////////////////

interface IProps {
    userId: string;
}

type LocalProps = IProps & CommonProps;

/////////////////////////////////////////////////////////////////////////////////////

interface ICreateTileProps {
    activeTile: DashboardUserEvolutionParticipationColumn;
    data: DashboardUserEvolutionParticipationResumeModel;
    onChange: (tile: DashboardUserEvolutionParticipationColumn) => void;
}

type CreateTileProps = ICreateTileProps & LocalProps;

class CreateTiles extends React.PureComponent<CreateTileProps>
{
    private handleTileClick(tile: DashboardUserEvolutionParticipationColumn) {
        this.props.onChange(tile);
    }

    public render() {
        const { data, activeTile } = this.props;

        let publications30 = 0, answers30 = 0;
        data.days.forEach((v) => {
            publications30 += v.publications;
            answers30 += v.answers;
        });

        var boxes: DashBoxListItem[] = [
            {
                key: DashboardUserEvolutionParticipationColumn.Publications,
                title: data.publications,
                subtitle: this.props.t("Views.Dashboards.UserEvolution.Participation.CreateTiles.PublicationsSubtitle"),
                description: this.props.t("Views.Dashboards.UserEvolution.Participation.CreateTiles.Description", { count: publications30 }),
                icon: faEdit,
            },
            {
                key: DashboardUserEvolutionParticipationColumn.Answers,
                title: data.answers,
                subtitle: this.props.t("Views.Dashboards.UserEvolution.Participation.CreateTiles.AnswersSubtitle"),
                description: this.props.t("Views.Dashboards.UserEvolution.Participation.CreateTiles.Description", { count: answers30 }),
                icon: faReplyAll,
            },
        ];

        return (
            <MuiContainer margin="normal">
                <DashListBox
                    color="warning"
                    boxes={boxes}
                    active={activeTile}
                    onClick={(key: DashboardUserEvolutionParticipationColumn) => this.handleTileClick(key)}
                />
            </MuiContainer>
        );
    }
}

////////////////////////////////////////////////////////////////////////////////////////////////////

interface IGraphProps {
    data: DashboardUserEvolutionParticipationResumeModel;
    tile: DashboardUserEvolutionParticipationColumn;
    onDayClick: (day: Date) => void;
}

type GraphProps = IGraphProps & CommonProps;

class CreateGraph extends React.PureComponent<GraphProps>  {

    render() {
        const { data: { days }, onDayClick, tile } = this.props;

        var ds: DashboardGraphModel[] = [];
        var t = DashboardUserEvolutionParticipationColumn[this.props.tile].toLowerCase();

        days.forEach((v, index) => {
            ds.push({ day: v.day, total: v[t] });
        });

        const title = tile == DashboardUserEvolutionParticipationColumn.Publications
            ? this.props.t("Views.Dashboards.UserEvolution.Participation.CreateGraph.PublicationsTitle")
            : tile == DashboardUserEvolutionParticipationColumn.Answers
                ? this.props.t("Views.Dashboards.UserEvolution.Participation.CreateGraph.AnswersTitle")
                : "";

        return (
            <>
                <MuiContainer margin="dense" >
                    <Typography component="p" variant="h6"><b>{title.toString().toLocaleUpperCase()}</b></Typography>
                    <MuiMuted>
                        <small><RenderGraphGmtInfo /></small>
                    </MuiMuted>
                </MuiContainer>

                <RenderLineGraph color="orange" data={ds} onSelectDay={onDayClick} />
            </>
        );
    };
}

/////////////////////////////////////////////////////////////////////////////////////////////

interface IListProps extends LocalProps {
    tile: DashboardUserEvolutionParticipationColumn;
    selectedDay?: Date;
    firstDay?: Date;
    lastDay?: Date;
    onCancelDay: () => void;
}

interface CreateListState {
    dialog: any;
    data: PagedResult<DashboardUserEvolutionParticiationListModel>;
    offset: number;
    loading: boolean;
}

class CreateList extends React.PureComponent<IListProps, CreateListState> {

    constructor(props: IListProps) {
        super(props);

        this.state = {
            dialog: null,
            data: null,
            offset: null,
            loading: true,
        };
    }

    ///
    private loadData(offset: number) {

        this.setState({ loading: true });

        api.getUserEvolutionParticipationAnalytic(
            {
                userId: this.props.userId,
                day: this.props.selectedDay ? this.props.selectedDay.convertUtcToLocalDate() : null,
                column: this.props.tile,
                range: { offset, take: DefaultListParameters.viewRecordsByPage }
            })
            .then((result) => {
                this.setState({ data: result, loading: false, offset: offset });
            })
            .catch((error) => {
                this.setState({ data: null, loading: false, offset: 0 });
            });
    }

    ///
    private renderTitle() {

        const title = this.props.tile == DashboardUserEvolutionParticipationColumn.Publications
            ? this.props.t("Views.Dashboards.UserEvolution.Participation.CreateList.PublicationsTitle")
            : this.props.tile == DashboardUserEvolutionParticipationColumn.Answers
                ? this.props.t("Views.Dashboards.UserEvolution.Participation.CreateList.AnswersTitle")
                : "";

        const sDay = this.props.selectedDay ? moment(this.props.selectedDay).format("DD/MMM") : null;

        return (
            <MuiContainer margin="dense" >
                <Grid container justifyContent="space-between" alignItems="center">
                    <Grid item>
                        <Typography variant="button">{title}</Typography>
                    </Grid>
                    <Grid item>

                        {
                            this.props.selectedDay &&
                            <MuiReverse color="warning">
                                <b>{`Dia ${sDay}`}</b>
                                <IconButtonCancel
                                    size="small"
                                    onClick={() => this.props.onCancelDay()}
                                />
                            </MuiReverse>
                        }
                    </Grid>
                </Grid>
            </MuiContainer>
        );
    }

    ///
    private renderList() {
        const { data: { items, total }, dialog, offset } = this.state;

        return (
            <MuiContainer margin="normal">
                <>
                    {dialog}
                    <br />
                    {this.renderTitle()}

                    {
                        this.props.selectedDay != null && total == 0
                            ? <MuiMessageBox
                                variant="warning"
                                message={
                                    this.props.t(
                                        this.props.tile == DashboardUserEvolutionParticipationColumn.Publications
                                            ? "Views.Dashboards.UserEvolution.Participation.CreateList.EmptyFilteredPublicationData"
                                            : "Views.Dashboards.UserEvolution.Participation.CreateList.EmptyFilteredAnswerData"
                                    )
                                }
                            />
                            : total == 0
                                ? <MuiMessageBox
                                    variant="warning"
                                    message={
                                        this.props.t(
                                            this.props.tile == DashboardUserEvolutionParticipationColumn.Publications
                                                ? "Views.Dashboards.UserEvolution.Participation.CreateList.EmptyPublicationData"
                                                : "Views.Dashboards.UserEvolution.Participation.CreateList.EmptyAnswerData"
                                        )
                                    }
                                />
                                : (
                                    <>
                                        <MuiContainer>
                                            <Table size="small" >
                                                <TableBody>
                                                    {
                                                        items.map((v, index) => {

                                                            var pubUrl = getPublicationUrl(v.publicationId, v.publicationType);
                                                            if (this.props.tile == DashboardUserEvolutionParticipationColumn.Answers) pubUrl += "?afu=" + Session.user.id;

                                                            return (
                                                                <TableRow key={index}>
                                                                    <TableCell style={{ width: 1, verticalAlign: "top" }}>{offset + index + 1}</TableCell>
                                                                    <TableCell style={{ width: 1 }}>
                                                                        <MuiLink href={pubUrl} target="_blank">
                                                                            {
                                                                                v.thumbnailUrl
                                                                                    ? <img src={v.thumbnailUrl} alt={v.topicName} style={{maxWidth: 64}} />
                                                                                    : <IconCreatePublication color="muted" size="3x" />
                                                                            }
                                                                        </MuiLink>
                                                                    </TableCell>
                                                                    <TableCell style={{ verticalAlign: "top"}}>
                                                                        <MuiLink href={pubUrl} color="secondary"  target="_blank">
                                                                            {v.title}
                                                                        </MuiLink>
                                                                        <MuiMuted><small>
                                                                            <div>{v.topicName}</div>
                                                                            <div>{Formaters.relativeTime(v.creationDate)}</div>
                                                                        </small></MuiMuted>
                                                                    </TableCell>
                                                                </TableRow>
                                                            );
                                                        })
                                                    }
                                                </TableBody>
                                            </Table>
                                        </MuiContainer>

                                        <MuiPagination
                                            limit={DefaultListParameters.viewRecordsByPage}
                                            offset={this.state.offset}
                                            total={total}
                                            onClick={(e, offset) => this.loadData(offset)}
                                        />
                                    </>
                                )
                    }
                </>
            </MuiContainer >
        );
    }

    ///
    public componentDidMount() {
        this.loadData(0);
    }

    //
    public componentDidUpdate(prevProps: IListProps) {
        if (
            (this.props.selectedDay && this.props.selectedDay != prevProps.selectedDay)
            ||
            (this.props.selectedDay == null && prevProps.selectedDay != null) || this.props.tile != prevProps.tile
        ) {
            this.loadData(0);
        }
    }

    ///
    public render() {

        if (this.state.loading)
            return <SpinnerIndicator />;

        if (this.state.data == null)
            return <AppMessageBox resource="LoadListDataFailed" />;

        return this.renderList();
    }
}

////////////////////////////////////////////////////////////////////////////////////////

interface UserEvolutionParticipationState {
    data: DashboardUserEvolutionParticipationResumeModel;
    loading: boolean;
    selectedDay?: Date;
    tile: DashboardUserEvolutionParticipationColumn;
}

class UserEvolutionParticipation extends React.PureComponent<LocalProps, UserEvolutionParticipationState> {

    constructor(props: LocalProps) {
        super(props);
        this.state = {
            data: null,
            loading: true,
            tile: DashboardUserEvolutionParticipationColumn.Publications,
        };
    }

    private loadData() {
        api.getUserEvolutionParticipationResume(this.props.userId)
            .then((result) => {

                result.days.forEach((v) => {
                    v.day = v.day.convertLocalToUtcDate();
                });

                this.setState({ data: result, loading: false });
            })
            .catch((error) => {
                this.setState({ data: null, loading: false });
            });
    }

    public componentDidMount() {
        this.loadData();
    }

    public render() {
        const { tile, data, loading } = this.state;

        if (loading)
            return <SpinnerIndicator />;

        if (data == null)
            return <AppMessageBox resource="LoadGraphDataFailed" />;

        const firstDay = data.days.length > 0 ? data.days[0].day : null;
        const lastDay = data.days.length > 0 ? data.days[data.days.length - 1].day : null;

        return (
            <>
                <CreateTiles
                    {...this.props}
                    userId={this.props.userId}
                    data={data}
                    activeTile={this.state.tile}
                    onChange={(tile) => this.setState({ tile: tile, selectedDay: null })}
                />

                <MuiContainer margin="normal">
                    <MuiPaper borderVariant="accentedRound" borderColor="warning">

                        <MuiContainer margin="normal">
                            <CreateGraph
                                {...this.props}
                                data={data}
                                tile={tile}
                                onDayClick={
                                    (day: Date) => {
                                        this.setState({ selectedDay: day });
                                    }
                                }
                            />
                        </MuiContainer>

                        <CreateList
                            {...this.props}
                            userId={this.props.userId}
                            tile={tile}
                            selectedDay={this.state.selectedDay}
                            firstDay={firstDay}
                            lastDay={lastDay}
                            onCancelDay={() => this.setState({ selectedDay: null })}
                        />

                    </MuiPaper>
                </MuiContainer>
            </>
        );
    }
}

export default UserEvolutionParticipation;